<template>
  <div class="alarm-classification-page">
    <div class="alarm-classification-header">
      <div class="header-title">
        <span class="title-text">报警分级管理</span>
        <span class="title-desc">统一查看气体超值、任务超期、证书临期、人员在岗、作业票和隐患治理报警</span>
      </div>
      <div class="header-actions">
        <Button @click="resetQuery">重置</Button>
        <Button type="primary" @click="searchList">查询</Button>
      </div>
    </div>

    <div class="alarm-classification-toolbar">
      <div class="toolbar-left">
        <div
          v-for="item in categoryOptions"
          :key="item.value"
          :class="['category-card', { active: activeCategory === item.value }]"
          @click="changeCategory(item.value)"
        >
          <div class="category-name">{{ item.label }}</div>
          <div class="category-total">{{ item.count }}</div>
        </div>
      </div>
      <div class="toolbar-right">
        <Input
          v-if="activeCategory === 'taskOverdue' || activeCategory === 'workPermit'"
          v-model="queryForm.keyword"
          search
          enter-button
          :placeholder="activeCategory === 'taskOverdue' ? '任务名称 / 编号 / 排查人' : '作业票名称 / 编号'"
          style="width: 260px"
          @on-search="searchList"
        />
        <Select
          v-if="currentCategoryConfig.supportStatus"
          v-model="queryForm.status"
          clearable
          style="width: 140px"
          placeholder="状态"
        >
          <Option v-for="item in currentStatusOptions" :key="item.value" :value="item.value">{{ item.label }}</Option>
        </Select>
        <DatePicker
          v-model="queryForm.dateRange"
          type="datetimerange"
          format="yyyy-MM-dd HH:mm:ss"
          placeholder="报警时间"
          style="width: 320px"
          @on-change="handleDateChange"
        />
      </div>
    </div>

    <div class="alarm-classification-content">
      <div class="content-side">
        <div class="side-title">报警类型</div>
        <div
          v-for="item in categoryOptions"
          :key="item.value + '-side'"
          :class="['side-item', { active: activeCategory === item.value }]"
          @click="changeCategory(item.value)"
        >
          <span>{{ item.label }}</span>
          <Tag>{{ item.count }}</Tag>
        </div>
      </div>

      <div class="content-main">
        <div class="content-main-toolbar">
          <div class="toolbar-summary">
            <span class="summary-title">{{ currentCategoryLabel }}</span>
            <span class="summary-count">共 {{ table.total }} 条</span>
          </div>
          <div class="toolbar-buttons">
            <Button v-if="currentCategoryConfig.supportHandle" type="primary" :disabled="!selectedRows.length" @click="batchHandle">
              批量处理
            </Button>
          </div>
        </div>

        <Table
          border
          :loading="table.loading"
          :columns="tableColumns"
          :data="table.data"
          @on-selection-change="handleSelectionChange"
        />

        <Page
          class="page-bar"
          :total="table.total"
          :current="table.thisPage"
          :page-size="table.limit"
          show-total
          @on-change="changePage"
        />
      </div>
    </div>

    <Modal v-model="detailModal.visible" title="报警详情" width="760" footer-hide>
      <div v-if="detailModal.row" class="detail-grid">
        <div v-for="(item, index) in currentDetailItems" :key="index" class="detail-item">
          <span class="detail-label">{{ item.label }}</span>
          <span class="detail-value">{{ item.value || '--' }}</span>
        </div>
      </div>
    </Modal>

    <Modal v-model="handleModal.visible" :title="handleModal.title" width="560" @on-ok="submitHandle" @on-cancel="resetHandleModal">
      <div v-if="handleModal.mode === 'gas'" class="handle-form">
        <div class="form-item">
          <div class="form-label">报警原因</div>
          <Input v-model="handleModal.form.because" type="textarea" :rows="3" placeholder="请输入报警原因" />
        </div>
        <div class="form-item">
          <div class="form-label">处理结果</div>
          <Input v-model="handleModal.form.dealContent" type="textarea" :rows="3" placeholder="请输入处理结果" />
        </div>
      </div>
      <div v-else-if="handleModal.mode === 'person'" class="handle-form">
        <div class="form-item">
          <div class="form-label">处理结果</div>
          <Input v-model="handleModal.form.disposeResult" type="textarea" :rows="4" placeholder="请输入处理结果" />
        </div>
      </div>
      <div v-else-if="handleModal.mode === 'video'" class="handle-form">
        <div class="form-item">
          <div class="form-label">报警状态</div>
          <Select v-model="handleModal.form.alarmStatus" style="width: 100%" placeholder="请选择状态">
            <Option :value="0">未处理</Option>
            <Option :value="1">已处理</Option>
          </Select>
        </div>
        <div class="form-item">
          <div class="form-label">备注</div>
          <Input v-model="handleModal.form.remark" type="textarea" :rows="4" placeholder="请输入备注" />
        </div>
      </div>
      <div v-else class="handle-empty">当前类型暂不支持处理。</div>
    </Modal>
  </div>
</template>

<script>
import {
  getPersonWarningList,
  handlePersonWarning,
  getEquipmentAlarm,
  handelEquipmentAlarm,
  getCertificateExpireList,
  getHiddenGovernList,
  getSbByList
} from '@/api/earlyWarning'
import { getTroubleshootOverdueList, getWorkPermitList } from '@/api/processManager'

const PERSON_SOURCE_TYPE_MAP = {
  0: '员工',
  1: '访客',
  2: '承包商',
  4: '区域超员',
  5: '电子围栏超员',
  20: '员工',
  21: '访客',
  22: '承包商',
  23: '物品',
  24: '车辆',
  25: '司机'
}

const PERSON_REASON_MAP = {
  1: '进入禁区',
  2: '车间超员',
  3: '进入车间超时',
  4: '进入车间未达标',
  5: '进入车间静止时间太长',
  6: '紧急报警',
  7: '电子围栏:时间过长',
  8: '电子围栏:时间过短',
  9: '电子围栏:静止过长',
  10: '电子围栏:人数超员',
  11: '操作工超员'
}

export default {
  name: 'AlarmClassification',
  data() {
    return {
      activeCategory: 'gas',
      queryForm: {
        status: '',
        keyword: '',
        dateRange: [],
        startTime: '',
        endTime: ''
      },
      categoryOptions: [
        { label: '气体超值报警', value: 'gas', count: 0, supportHandle: true, handleMode: 'gas', supportStatus: true },
        { label: '任务超期报警', value: 'taskOverdue', count: 0, supportHandle: false, handleMode: '', supportStatus: false, detailRoute: '/processManager/dangerTask' },
        { label: '证书临期报警', value: 'certificateExpiry', count: 0, supportHandle: false, handleMode: '', supportStatus: false, detailRoute: '/processManager/certificate' },
        { label: '人员在岗报警', value: 'onDuty', count: 0, supportHandle: true, handleMode: 'person', supportStatus: true },
        { label: '作业票报警', value: 'workPermit', count: 0, supportHandle: false, handleMode: '', supportStatus: false, detailRoute: '/processManager/workPermit' },
        { label: '隐患治理报警', value: 'hiddenDanger', count: 0, supportHandle: false, handleMode: '', supportStatus: true }
      ],
      selectedRows: [],
      table: {
        loading: false,
        data: [],
        total: 0,
        limit: 10,
        thisPage: 1
      },
      detailModal: {
        visible: false,
        row: null
      },
      handleModal: {
        visible: false,
        mode: '',
        title: '报警处理',
        rows: [],
        form: {}
      }
    }
  },
  computed: {
    currentCategoryConfig() {
      return this.categoryOptions.find(item => item.value === this.activeCategory) || this.categoryOptions[0]
    },
    currentCategoryLabel() {
      return this.currentCategoryConfig ? this.currentCategoryConfig.label : '报警管理'
    },
    currentStatusOptions() {
      if (this.activeCategory === 'gas') {
        return [
          { label: '未处理', value: 0 },
          { label: '已处理', value: 1 },
          { label: '已忽略', value: 2 }
        ]
      }
      if (this.activeCategory === 'onDuty') {
        return [
          { label: '未处理', value: 0 },
          { label: '已处理', value: 1 }
        ]
      }
      if (this.activeCategory === 'hiddenDanger') {
        return [
          { label: '全部状态', value: '' },
          { label: '已创建', value: 0 },
          { label: '待审批', value: 1 },
          { label: '待整改', value: 2 },
          { label: '待复核', value: 3 }
        ]
      }
      return []
    },
    tableColumns() {
      return this.buildColumns()
    },
    currentDetailItems() {
      if (!this.detailModal.row) {
        return []
      }
      return this.buildDetailItems(this.detailModal.row)
    }
  },
  mounted() {
    this.loadList()
    this.loadCategoryCounts()
  },
  methods: {
    buildColumns() {
      if (this.activeCategory === 'certificateExpiry') {
        return [
          {
            title: '序号',
            width: 70,
            align: 'center',
            render: (h, params) => h('span', params.index + 1 + (this.table.thisPage - 1) * this.table.limit)
          },
          {
            title: '证书编号',
            minWidth: 180,
            render: (h, params) => h('span', this.getCertificateFieldValue(params.row, 'certificateNo'))
          },
          {
            title: '证书名称',
            minWidth: 160,
            render: (h, params) => h('span', this.getCertificateFieldValue(params.row, 'certificateName'))
          },
          {
            title: '发证机关',
            minWidth: 160,
            render: (h, params) => h('span', this.getCertificateFieldValue(params.row, 'issuingAuthority'))
          },
          {
            title: '所属单位',
            minWidth: 160,
            render: (h, params) => h('span', this.getCertificateCompanyText(params.row))
          },
          {
            title: '拥有者',
            minWidth: 130,
            render: (h, params) => h('span', this.getCertificateFieldValue(params.row, 'ownerEmployeeName'))
          },
          {
            title: '批准项目',
            minWidth: 180,
            render: (h, params) => h('span', this.getCertificateFieldValue(params.row, 'authorizeProject'))
          },
          {
            title: '证书类别',
            minWidth: 130,
            render: (h, params) => h('span', this.getCertificateFieldValue(params.row, 'certificateTypeName'))
          },
          {
            title: '发证日期',
            minWidth: 150,
            render: (h, params) => h('span', this.formatCertificateDate(params.row.getCertificateTime))
          },
          {
            title: '到期时间',
            minWidth: 150,
            render: (h, params) => h('span', this.formatCertificateDate(params.row.expireTime))
          },
          {
            title: '下次复审时间',
            minWidth: 150,
            render: (h, params) => h('span', this.formatCertificateDate(params.row.nextReviewTime))
          },
          {
            title: '剩余时间',
            minWidth: 120,
            render: (h, params) => h('Tag', { props: { color: 'red' } }, params.row.days || '--')
          },
          {
            title: '操作',
            width: 80,
            align: 'center',
            render: (h, params) => h(
              'Button',
              {
                props: { type: 'text' },
                on: { click: () => this.showDetail(params.row) }
              },
              '详情'
            )
          }
        ]
      }
      if (this.activeCategory === 'taskOverdue') {
        return [
          {
            title: '序号',
            width: 70,
            align: 'center',
            render: (h, params) => h('span', params.index + 1 + (this.table.thisPage - 1) * this.table.limit)
          },
          {
            title: '任务编号',
            minWidth: 180,
            render: (h, params) => h('span', this.getRecordId(params.row))
          },
          {
            title: '任务名称',
            minWidth: 180,
            render: (h, params) => h('span', params.row.planName || '--')
          },
          {
            title: '排查人',
            minWidth: 140,
            render: (h, params) => h('span', this.getHandlerName(params.row))
          },
          {
            title: '计划开始时间',
            minWidth: 170,
            render: (h, params) => h('span', this.formatDateValue(params.row.planStartTime))
          },
          {
            title: '计划完成时间',
            minWidth: 170,
            render: (h, params) => h('span', this.formatDateValue(params.row.planEndTime))
          },
          {
            title: '实际开始时间',
            minWidth: 170,
            render: (h, params) => h('span', this.formatDateValue(params.row.startTime))
          },
          {
            title: '实际完成时间',
            minWidth: 170,
            render: (h, params) => h('span', this.formatDateValue(params.row.endTime))
          },
          {
            title: '状态',
            minWidth: 120,
            render: (h, params) => h('Tag', { props: { color: this.getStatusColor(params.row) } }, this.getStatusText(params.row))
          },
          {
            title: '操作',
            width: 80,
            align: 'center',
            render: (h, params) => h(
              'Button',
              {
                props: { type: 'text' },
                on: { click: () => this.showDetail(params.row) }
              },
              '详情'
            )
          }
        ]
      }
      if (this.activeCategory === 'workPermit') {
        return [
          {
            title: '序号',
            width: 70,
            align: 'center',
            render: (h, params) => h('span', params.index + 1 + (this.table.thisPage - 1) * this.table.limit)
          },
          {
            title: '作业票编号',
            minWidth: 160,
            render: (h, params) => h('span', params.row.no || '--')
          },
          {
            title: '作业票名称',
            minWidth: 180,
            render: (h, params) => h('span', params.row.name || '--')
          },
          {
            title: '作业地点',
            minWidth: 160,
            render: (h, params) => h('span', params.row.place || '--')
          },
          {
            title: '计划开始时间',
            minWidth: 170,
            render: (h, params) => h('span', this.formatDateValue(params.row.planStartTime))
          },
          {
            title: '计划完成时间',
            minWidth: 170,
            render: (h, params) => h('span', this.formatDateValue(params.row.planEndTime))
          },
          {
            title: '实际开始时间',
            minWidth: 170,
            render: (h, params) => h('span', this.formatDateValue(params.row.actualStartTime))
          },
          {
            title: '实际完成时间',
            minWidth: 170,
            render: (h, params) => h('span', this.formatDateValue(params.row.actualEndTime))
          },
          {
            title: '状态',
            minWidth: 110,
            render: (h, params) => h('Tag', { props: { color: this.getStatusColor(params.row) } }, this.getStatusText(params.row))
          },
          {
            title: '操作',
            width: 80,
            align: 'center',
            render: (h, params) => h(
              'Button',
              {
                props: { type: 'text' },
                on: { click: () => this.showDetail(params.row) }
              },
              '详情'
            )
          }
        ]
      }
      if (this.activeCategory === 'hiddenDanger') {
        return [
          {
            title: '序号',
            width: 70,
            align: 'center',
            render: (h, params) => h('span', params.index + 1 + (this.table.thisPage - 1) * this.table.limit)
          },
          {
            title: '隐患治理编号',
            minWidth: 170,
            render: (h, params) => h('span', this.getRecordId(params.row))
          },
          {
            title: '状态',
            minWidth: 100,
            render: (h, params) => h('Tag', { props: { color: this.getStatusColor(params.row) } }, this.getStatusText(params.row))
          },
          {
            title: '隐患名称',
            minWidth: 160,
            render: (h, params) => h('span', params.row.hdName || '--')
          },
          {
            title: '隐患级别',
            minWidth: 110,
            render: (h, params) => h('Tag', { props: { color: this.getLevelColor(params.row) } }, this.getLevelText(params.row))
          },
          {
            title: '检查时间',
            minWidth: 170,
            render: (h, params) => h('span', params.row.checkTime || '--')
          },
          {
            title: '计划整改时间',
            minWidth: 170,
            render: (h, params) => h('span', params.row.planRectificationTime || '--')
          },
          {
            title: '检查部位',
            minWidth: 150,
            render: (h, params) => h('span', params.row.checkParts || '--')
          },
          {
            title: '存在问题和隐患',
            minWidth: 260,
            render: (h, params) => h('span', params.row.problem || '--')
          },
          {
            title: '创建人',
            minWidth: 110,
            render: (h, params) => h('span', this.getHandlerName(params.row))
          },
          {
            title: '整改人',
            minWidth: 110,
            render: (h, params) => h('span', params.row.rectifierVo && params.row.rectifierVo.nickname ? params.row.rectifierVo.nickname : '--')
          },
          {
            title: '来源',
            minWidth: 130,
            render: (h, params) => h('span', params.row.originate || '--')
          },
          {
            title: '操作',
            width: 80,
            align: 'center',
            render: (h, params) => h(
              'Button',
              {
                props: { type: 'text' },
                on: { click: () => this.showDetail(params.row) }
              },
              '详情'
            )
          }
        ]
      }
      const columns = []
      if (this.currentCategoryConfig.supportHandle) {
        columns.push({ type: 'selection', width: 60, align: 'center' })
      }
      columns.push(
        {
          title: '序号',
          width: 70,
          align: 'center',
          render: (h, params) => h('span', params.index + 1 + (this.table.thisPage - 1) * this.table.limit)
        },
        {
          title: '报警记录编号',
          minWidth: 180,
          render: (h, params) => h('span', this.getRecordId(params.row))
        },
        {
          title: '报警类型',
          minWidth: 130,
          render: (h) => h('span', this.currentCategoryLabel)
        },
        {
          title: '报警内容',
          minWidth: 260,
          render: (h, params) => h('span', this.getAlarmContent(params.row))
        },
        {
          title: '报警等级',
          minWidth: 120,
          render: (h, params) => h('Tag', { props: { color: this.getLevelColor(params.row) } }, this.getLevelText(params.row))
        },
        {
          title: '状态',
          minWidth: 110,
          render: (h, params) => h('Tag', { props: { color: this.getStatusColor(params.row) } }, this.getStatusText(params.row))
        },
        {
          title: '报警时间',
          minWidth: 170,
          render: (h, params) => h('span', this.getAlarmTime(params.row))
        },
        {
          title: '更新时间',
          minWidth: 170,
          render: (h, params) => h('span', this.getHandleTime(params.row))
        },
        {
          title: '处理人',
          minWidth: 120,
          render: (h, params) => h('span', this.getHandlerName(params.row))
        },
        {
          title: '操作',
          width: this.currentCategoryConfig.supportHandle ? 140 : 80,
          align: 'center',
          render: (h, params) => {
            const children = [
              h(
                'Button',
                {
                  props: { type: 'text' },
                  on: { click: () => this.showDetail(params.row) }
                },
                '详情'
              )
            ]
            if (this.currentCategoryConfig.supportHandle) {
              children.push(
                h(
                  'Button',
                  {
                    props: { type: 'text', disabled: !this.canHandle(params.row) },
                    on: { click: () => this.openHandleModal([params.row]) }
                  },
                  '处理'
                )
              )
            }
            return h('div', children)
          }
        }
      )
      return columns
    },
    loadCategoryCounts() {
      const baseParams = { curPage: 1, limit: 1 }
      const countRequests = {
        gas: getEquipmentAlarm(baseParams).then(res => this.normalizeListResult(res)),
        taskOverdue: getTroubleshootOverdueList(Object.assign({}, baseParams, { overdueOnly: 1 })).then(res => this.normalizeListResult(res)),
        certificateExpiry: getCertificateExpireList(baseParams).then(res => this.normalizeListResult(res)),
        onDuty: getPersonWarningList(baseParams).then(res => this.normalizeListResult(res)),
        workPermit: getWorkPermitList(Object.assign({}, baseParams, { overdueOnly: 1 })).then(res => this.normalizeListResult(res)),
        hiddenDanger: getHiddenGovernList(Object.assign({}, baseParams, { alarmOnly: 1 })).then(res => this.normalizeListResult(res))
      }
      Object.keys(countRequests).forEach(type => {
        countRequests[type]
          .then(result => {
            const total = typeof result.total === 'number' ? result.total : 0
            this.updateCategoryCount(type, total)
          })
          .catch(() => {
            this.updateCategoryCount(type, 0)
          })
      })
    },
    loadList() {
      this.table.loading = true
      const params = this.buildQueryParams()
      this.fetchCategoryData(params)
        .then(result => {
          const list = this.activeCategory === 'hiddenDanger'
            ? (Array.isArray(result.list) ? result.list : [])
            : this.applyClientFilters(Array.isArray(result.list) ? result.list : [])
          const total = typeof result.total === 'number' ? result.total : list.length

          this.table.data = list
          this.table.total = total
          this.selectedRows = []
          this.updateCategoryCount(this.activeCategory, total)
        })
        .catch(() => {
          this.table.data = []
          this.table.total = 0
          this.selectedRows = []
        })
        .finally(() => {
          this.table.loading = false
        })
    },
    fetchCategoryData(params) {
      if (this.activeCategory === 'gas') {
        return getEquipmentAlarm(params).then(res => this.normalizeListResult(res))
      }
      if (this.activeCategory === 'taskOverdue') {
        return getTroubleshootOverdueList(params).then(res => this.normalizeListResult(res))
      }
      if (this.activeCategory === 'certificateExpiry') {
        return this.fetchCertificateExpiryList(params)
      }
      if (this.activeCategory === 'onDuty') {
        return getPersonWarningList(params).then(res => this.normalizeListResult(res))
      }
      if (this.activeCategory === 'workPermit') {
        return getWorkPermitList(params).then(res => this.normalizeListResult(res))
      }
      if (this.activeCategory === 'hiddenDanger') {
        const hiddenGovernParams = {
          curPage: params.curPage,
          limit: params.limit
        }
        hiddenGovernParams.alarmOnly = 1
        if (params.status !== undefined && params.status !== null && params.status !== '') {
          hiddenGovernParams.status = params.status
        }
        if (params.startTime) {
          hiddenGovernParams.checkStartTime = params.startTime
        }
        if (params.endTime) {
          hiddenGovernParams.checkEndTime = params.endTime
        }
        return getHiddenGovernList(hiddenGovernParams).then(res => this.normalizeListResult(res))
      }
      return Promise.resolve({ list: [], total: 0 })
    },
    fetchCertificateExpiryList(params) {
      return getCertificateExpireList({
        curPage: params.curPage,
        limit: params.limit
      }).then(res => {
        const list = this.normalizeListResult(res)
        return {
          list: Array.isArray(list.list) ? list.list : [],
          total: typeof list.total === 'number' ? list.total : 0
        }
      })
    },

    normalizeListResult(res) {
      const body = res && res.body ? res.body : {}
      return {
        list: Array.isArray(body.data) ? body.data : [],
        total: Number(body.cum || body.total || 0)
      }
    },
    buildQueryParams() {
      const params = {
        curPage: this.table.thisPage,
        limit: this.table.limit
      }
      if (this.activeCategory === 'taskOverdue') {
        params.overdueOnly = 1
        if (this.queryForm.keyword) {
          params.keyword = this.queryForm.keyword
          params.name = this.queryForm.keyword
        }
        if (this.queryForm.startTime) {
          params.planStartTime = this.queryForm.startTime
        }
        if (this.queryForm.endTime) {
          params.planEndTime = this.queryForm.endTime
        }
        return params
      }
      if (this.activeCategory === 'workPermit') {
        params.overdueOnly = 1
        if (this.queryForm.keyword) {
          params.searchParam = this.queryForm.keyword
        }
        if (this.queryForm.startTime) {
          params.planStartTime = this.queryForm.startTime
        }
        if (this.queryForm.endTime) {
          params.planEndTime = this.queryForm.endTime
        }
        return params
      }
      if (this.currentCategoryConfig.supportStatus && this.queryForm.status !== '' && this.queryForm.status !== null && this.queryForm.status !== undefined) {
        params.status = this.queryForm.status
      }
      if (this.queryForm.startTime) {
        params.startTime = this.queryForm.startTime
      }
      if (this.queryForm.endTime) {
        params.endTime = this.queryForm.endTime
      }
      return params
    },
    applyClientFilters(list) {
      return list.filter(row => this.matchStatus(row) && this.matchDate(row))
    },
    matchStatus(row) {
      if (this.queryForm.status === '' || this.queryForm.status === null || this.queryForm.status === undefined) {
        return true
      }
      if (!this.currentCategoryConfig.supportStatus) {
        return true
      }
      const status = this.getStatusValue(row)
      return String(status) === String(this.queryForm.status)
    },
    matchDate(row) {
      if (!this.queryForm.startTime && !this.queryForm.endTime) {
        return true
      }
      const timeValue = this.parseTimeValue(this.getAlarmTime(row))
      const startValue = this.parseTimeValue(this.queryForm.startTime)
      const endValue = this.parseTimeValue(this.queryForm.endTime)
      if (!timeValue) {
        return false
      }
      if (startValue && timeValue < startValue) {
        return false
      }
      if (endValue && timeValue > endValue) {
        return false
      }
      return true
    },
    parseTimeValue(value) {
      if (!value) {
        return 0
      }
      if (typeof value === 'number') {
        return value
      }
      const parsed = new Date(value).getTime()
      return Number.isNaN(parsed) ? 0 : parsed
    },
    updateCategoryCount(type, count) {
      this.categoryOptions = this.categoryOptions.map(item => {
        if (item.value === type) {
          return Object.assign({}, item, { count: count })
        }
        return item
      })
    },
    handleDateChange(value) {
      this.queryForm.startTime = value && value[0] ? value[0] : ''
      this.queryForm.endTime = value && value[1] ? value[1] : ''
    },
    changeCategory(value) {
      if (this.activeCategory === value) {
        return
      }
      this.activeCategory = value
      this.table.thisPage = 1
      this.selectedRows = []
      this.queryForm.status = ''
      this.queryForm.keyword = ''
      this.loadList()
    },
    searchList() {
      this.table.thisPage = 1
      this.loadList()
    },
    resetQuery() {
      this.queryForm.status = ''
      this.queryForm.keyword = ''
      this.queryForm.dateRange = []
      this.queryForm.startTime = ''
      this.queryForm.endTime = ''
      this.table.thisPage = 1
      this.loadList()
    },
    changePage(page) {
      this.table.thisPage = page
      this.loadList()
    },
    handleSelectionChange(selection) {
      this.selectedRows = selection
    },
    showDetail(row) {
      const shouldNavigateToManagement = ['certificateExpiry', 'taskOverdue', 'workPermit'].includes(this.activeCategory)
      if (shouldNavigateToManagement && this.currentCategoryConfig.detailRoute) {
        if (this.activeCategory === 'workPermit' && !row.num) {
          this.$Message.warning('未获取到作业票编号')
          return
        }
        if (this.activeCategory !== 'workPermit' && !row.id) {
          this.$Message.warning(this.activeCategory === 'taskOverdue' ? '未获取到排查任务信息' : '未获取到证书信息')
          return
        }
        this.$router.push({
          path: this.currentCategoryConfig.detailRoute,
          query: this.activeCategory === 'taskOverdue'
            ? {
              taskId: row.id,
              taskNum: row.taskNum || ''
            }
            : this.activeCategory === 'certificateExpiry'
              ? {
                certificateId: row.id,
                certificateNo: row.certificateNo || ''
              }
              : {
                workPermitNo: row.num
              }
        })
        return
      }
      this.detailModal.row = row
      this.detailModal.visible = true
    },
    buildDetailItems(row) {
      if (this.activeCategory === 'gas') {
        return [
          { label: '报警编号', value: this.getRecordId(row) },
          { label: '采集点名称', value: row.tagName || row.channelName || '--' },
          { label: '报警位置', value: row.location || '--' },
          { label: '报警数值', value: this.getGasValueText(row) },
          { label: '报警类型', value: this.getGasAlertTypeText(row) },
          { label: '报警等级', value: this.getLevelText(row) },
          { label: '状态', value: this.getStatusText(row) },
          { label: '报警时间', value: this.getAlarmTime(row) },
          { label: '通知人', value: this.getHandlerName(row) }
        ]
      }
      if (this.activeCategory === 'certificateExpiry') {
        return [
          { label: '证书编号', value: row.certificateNo || row.id || '--' },
          { label: '证书名称', value: row.certificateName || '--' },
          { label: '发证机关', value: row.issuingAuthority || '--' },
          { label: '所属单位', value: this.getCertificateCompanyText(row) },
          { label: '拥有者', value: row.ownerEmployeeName || '--' },
          { label: '批准项目', value: row.authorizeProject || '--' },
          { label: '证书类别', value: row.certificateTypeName || '--' },
          { label: '发证日期', value: this.formatCertificateDate(row.getCertificateTime) },
          { label: '到期时间', value: this.formatCertificateDate(row.expireTime) },
          { label: '下次复审时间', value: this.formatCertificateDate(row.nextReviewTime) },
          { label: '剩余时间', value: row.days || '--' }
        ]
      }
      if (this.activeCategory === 'onDuty') {
        return [
          { label: '报警编号', value: this.getRecordId(row) },
          { label: '报警来源', value: row.warningSource || '--' },
          { label: '来源类型', value: this.getPersonSourceTypeText(row.sourceType) },
          { label: '定位器编号', value: row.pointNo || '--' },
          { label: '报警原因', value: this.getPersonReasonText(row.warningReason) },
          { label: '报警区域', value: row.warningArea || '--' },
          { label: '状态', value: this.getStatusText(row) }
        ]
      }
      if (this.activeCategory === 'workPermit') {
        return [
          { label: '报警编号', value: this.getRecordId(row) },
          { label: '作业名称', value: row.name || '--' },
          { label: '状态', value: this.getWorkPermitStatusText(row) },
          { label: '通知人', value: row.noticePerson || '--' },
          { label: '更新时间', value: this.getHandleTime(row) }
        ]
      }
      if (this.activeCategory === 'taskOverdue') {
        return [
          { label: '任务编号', value: this.getRecordId(row) },
          { label: '任务名称', value: row.planName || '--' },
          { label: '排查人', value: this.getHandlerName(row) },
          { label: '计划开始时间', value: this.formatDateValue(row.planStartTime) },
          { label: '计划完成时间', value: this.formatDateValue(row.planEndTime) },
          { label: '实际开始时间', value: this.formatDateValue(row.startTime) },
          { label: '实际完成时间', value: this.formatDateValue(row.endTime) },
          { label: '状态', value: this.getStatusText(row) },
          { label: '创建人', value: row.creator || '--' }
        ]
      }
      if (this.activeCategory === 'hiddenDanger') {
        return [
          { label: '隐患治理编号', value: this.getRecordId(row) },
          { label: '隐患名称', value: row.hdName || '--' },
          { label: '隐患级别', value: this.getLevelText(row) },
          { label: '检查部位', value: row.checkParts || '--' },
          { label: '存在问题和隐患', value: row.problem || '--' },
          { label: '检查时间', value: row.checkTime || '--' },
          { label: '计划整改时间', value: row.planRectificationTime || '--' },
          { label: '创建人', value: this.getHandlerName(row) },
          { label: '整改人', value: row.rectifierVo && row.rectifierVo.nickname ? row.rectifierVo.nickname : '--' },
          { label: '状态', value: this.getStatusText(row) }
        ]
      }
      return []
    },
    canHandle(row) {
      if (!this.currentCategoryConfig.supportHandle) {
        return false
      }
      if (this.activeCategory === 'gas') {
        const status = this.getStatusValue(row)
        return status === 0 || status === '0' || status === '' || status === null || status === undefined
      }
      if (this.activeCategory === 'taskOverdue') {
        const status = this.getStatusValue(row)
        return status === 0 || status === '0' || status === '' || status === null || status === undefined
      }
      if (this.activeCategory === 'onDuty') {
        const status = this.getStatusValue(row)
        return status === 0 || status === '0' || status === '' || status === null || status === undefined
      }
      return false
    },
    batchHandle() {
      if (!this.selectedRows.length) {
        this.$Message.warning('请选择报警记录')
        return
      }
      this.openHandleModal(this.selectedRows)
    },
    openHandleModal(rows) {
      if (!rows || !rows.length) {
        this.$Message.warning('请选择报警记录')
        return
      }
      this.handleModal.rows = rows
      this.handleModal.mode = this.currentCategoryConfig.handleMode
      this.handleModal.title = this.currentCategoryLabel + '处理'
      if (this.handleModal.mode === 'gas') {
        this.handleModal.form = {
          because: '',
          dealContent: ''
        }
      } else if (this.handleModal.mode === 'person') {
        this.handleModal.form = {
          disposeResult: ''
        }
      } else if (this.handleModal.mode === 'video') {
        this.handleModal.form = {
          alarmStatus: 1,
          remark: ''
        }
      } else {
        this.handleModal.form = {}
      }
      this.handleModal.visible = true
    },
    resetHandleModal() {
      this.handleModal.visible = false
      this.handleModal.rows = []
      this.handleModal.mode = ''
      this.handleModal.title = '报警处理'
      this.handleModal.form = {}
    },
    submitHandle() {
      if (!this.handleModal.rows.length) {
        this.$Message.warning('请选择报警记录')
        return
      }
      let request = null
      if (this.handleModal.mode === 'gas') {
        if (!this.handleModal.form.because || !this.handleModal.form.dealContent) {
          this.$Message.warning('请填写报警原因和处理结果')
          return
        }
        request = Promise.all(this.handleModal.rows.map(row => {
          return handelEquipmentAlarm({
            because: this.handleModal.form.because,
            dealContent: this.handleModal.form.dealContent,
            id: this.getHandleId(row)
          })
        }))
      } else if (this.handleModal.mode === 'person') {
        if (!this.handleModal.form.disposeResult) {
          this.$Message.warning('请填写处理结果')
          return
        }
        request = Promise.all(this.handleModal.rows.map(row => {
          return handlePersonWarning({
            disposeResult: this.handleModal.form.disposeResult,
            id: this.getHandleId(row)
          })
        }))
      } else if (this.handleModal.mode === 'video') {
        request = Promise.all(this.handleModal.rows.map(row => {
          return nvrAlarmUpdate(Object.assign({}, row, {
            alarmStatus: this.handleModal.form.alarmStatus,
            remark: this.handleModal.form.remark
          }))
        }))
      } else {
        this.$Message.warning('当前类型暂不支持处理')
        return
      }

      request.then(() => {
        this.$Message.success('处理成功')
        this.resetHandleModal()
        this.loadList()
      })
    },
    getHandleId(row) {
      return row.id || row.recordId || row.warningId || row.channelRecordId || row.personWarningId || ''
    },
    getRecordId(row) {
      if (this.activeCategory === 'gas') {
        return row.num || row.tagId || row.recordId || row.id || '--'
      }
      if (this.activeCategory === 'taskOverdue') {
        return row.taskNum || row.id || '--'
      }
      if (this.activeCategory === 'certificateExpiry') {
        return row.certificateNo || row.id || '--'
      }
      if (this.activeCategory === 'onDuty') {
        return row.id || row.warningNum || '--'
      }
      if (this.activeCategory === 'workPermit') {
        return row.num || row.id || '--'
      }
      if (this.activeCategory === 'hiddenDanger') {
        return row.num || row.hiddenGovernNum || row.id || '--'
      }
      return row.id || '--'
    },
    getAlarmContent(row) {
      if (this.activeCategory === 'gas') {
        return this.joinText([
          row.tagName || row.channelName,
          row.location,
          this.getGasValueText(row),
          this.getGasAlertTypeText(row)
        ])
      }
      if (this.activeCategory === 'taskOverdue') {
        return this.joinText([row.taskNum, row.planName, row.troubleshootEmployeeName, this.formatDateValue(row.planEndTime)])
      }
      if (this.activeCategory === 'certificateExpiry') {
        return this.joinText([row.certificateName, row.certificateNo, row.issuingAuthority, row.nextReviewTime])
      }
      if (this.activeCategory === 'onDuty') {
        return this.joinText([
          this.getPersonReasonText(row.warningReason),
          row.warningArea,
          row.warningSource
        ])
      }
      if (this.activeCategory === 'workPermit') {
        return this.joinText([row.name, this.getWorkPermitStatusText(row), row.noticePerson])
      }
      if (this.activeCategory === 'hiddenDanger') {
        return this.joinText([row.hdName, row.problem, row.checkParts])
      }
      return '--'
    },
    joinText(list) {
      const values = list.filter(item => item !== '' && item !== null && item !== undefined && item !== '--')
      return values.length ? values.join(' / ') : '--'
    },
    getGasValueText(row) {
      const value = row.value !== undefined && row.value !== null ? row.value : '--'
      const unit = row.unit || ''
      return '' + value + unit
    },
    getGasAlertTypeText(row) {
      const type = row.overType !== undefined && row.overType !== null ? row.overType : row.alertType
      if (type === -1 || type === '-1' || type === 4 || type === '4') {
        return '低低报'
      }
      if (type === 0 || type === '0' || type === 3 || type === '3') {
        return '低报'
      }
      if (type === 2 || type === '2' || type === 1 || type === '1') {
        return '高高报'
      }
      if (type === 1 || type === '1' || type === 2 || type === '2') {
        return '高报'
      }
      if (type === 5 || type === '5') {
        return '设备故障'
      }
      return '--'
    },
    getPersonSourceTypeText(value) {
      return PERSON_SOURCE_TYPE_MAP[value] || value || '--'
    },
    getPersonReasonText(value) {
      return PERSON_REASON_MAP[value] || value || '--'
    },
    getCertificateFieldValue(row, ...keys) {
      for (const key of keys) {
        if (row[key] !== undefined && row[key] !== null && row[key] !== '') {
          return row[key]
        }
      }
      return '--'
    },
    getCertificateCompanyText(row) {
      if (row.type === 0 || row.type === '0') {
        return '本企业'
      }
      if (row.type === 1 || row.type === '1') {
        return row.affiliatedCompanies || '--'
      }
      return row.affiliatedCompanies || '--'
    },
    formatCertificateDate(value) {
      if (!value) {
        return '--'
      }
      if (typeof value === 'string') {
        return value.split(' ')[0]
      }
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) {
        return String(value)
      }
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    formatDateValue(value) {
      if (!value) {
        return '--'
      }
      if (typeof value === 'string') {
        return value
      }
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) {
        return String(value)
      }
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    },
    getStatusValue(row) {
      if (this.activeCategory === 'gas') {
        if (row.status !== undefined && row.status !== null) {
          return row.status
        }
        if (row.isDeal !== undefined && row.isDeal !== null) {
          return row.isDeal
        }
        if (row.disposeStatus !== undefined && row.disposeStatus !== null) {
          return row.disposeStatus
        }
        return ''
      }
      if (this.activeCategory === 'taskOverdue') {
        return row.status !== undefined && row.status !== null ? row.status : ''
      }
      if (this.activeCategory === 'onDuty') {
        if (row.warningStatus !== undefined && row.warningStatus !== null) {
          return row.warningStatus
        }
        if (row.status !== undefined && row.status !== null) {
          return row.status
        }
        return ''
      }
      if (this.activeCategory === 'hiddenDanger') {
        return row.status !== undefined && row.status !== null ? row.status : ''
      }
      return row.status !== undefined && row.status !== null ? row.status : ''
    },
    getWorkPermitStatusText(row) {
      const status = row.status
      if (status === 0 || status === '0') {
        return '已创建'
      }
      if (status === 7 || status === '7') {
        return '待发送'
      }
      if (status === 1 || status === '1') {
        return '审核中'
      }
      if (status === 2 || status === '2') {
        return '待作业'
      }
      if (status === 3 || status === '3') {
        return '作业中'
      }
      if (status === 4 || status === '4') {
        if (row.acceptanceStatus === 2 || row.acceptanceStatus === '2') {
          return '验收未通过'
        }
        return '待验收'
      }
      if (status === 5 || status === '5') {
        if (row.isQualified === 2 || row.isQualified === '2') {
          return '复核不合格'
        }
        return '待复核'
      }
      if (status === 6 || status === '6') {
        return '完成'
      }
      return '--'
    },
    getStatusText(row) {
      const status = this.getStatusValue(row)
      if (this.activeCategory === 'gas') {
        if (status === 0 || status === '0') {
          return '未处理'
        }
        if (status === 1 || status === '1') {
          return '已处理'
        }
        if (status === 2 || status === '2') {
          return '已忽略'
        }
        return '--'
      }
      if (this.activeCategory === 'taskOverdue') {
        if (status === 0 || status === '0') {
          return '超期未完成'
        }
        if (status === 1 || status === '1') {
          return '已完成'
        }
        return '--'
      }
      if (this.activeCategory === 'onDuty') {
        if (status === 0 || status === '0') {
          return '未处理'
        }
        if (status === 1 || status === '1') {
          return '已处理'
        }
        return '--'
      }
      if (this.activeCategory === 'workPermit') {
        return this.getWorkPermitStatusText(row)
      }
      if (this.activeCategory === 'certificateExpiry') {
        return '待处理'
      }
      if (this.activeCategory === 'hiddenDanger') {
        if (status === 0 || status === '0') {
          return '已创建'
        }
        if (status === 1 || status === '1') {
          return '待审批'
        }
        if (status === 2 || status === '2') {
          return '待整改'
        }
        if (status === 3 || status === '3') {
          return '待复核'
        }
        if (status === 4 || status === '4') {
          return '已完成'
        }
        return '--'
      }
      return status || '--'
    },
    getStatusColor(row) {
      const text = this.getStatusText(row)
      if (text === '未处理' || text === '待处理' || text === '超期未完成') {
        return 'error'
      }
      if (text === '已处理' || text === '已完成') {
        return 'success'
      }
      if (text === '已忽略') {
        return 'default'
      }
      if (text === '审核中') {
        return 'warning'
      }
      if (text === '待审批') {
        return 'primary'
      }
      if (text === '待整改') {
        return 'error'
      }
      if (text === '待复核') {
        return 'warning'
      }
      if (text === '已完成') {
        return 'success'
      }
      return 'default'
    },
    getLevelValue(row) {
      if (row.level !== undefined && row.level !== null) {
        return row.level
      }
      if (row.warningLevel !== undefined && row.warningLevel !== null) {
        return row.warningLevel
      }
      if (row.priority !== undefined && row.priority !== null) {
        return row.priority
      }
      return ''
    },
    getLevelText(row) {
      if (this.activeCategory === 'gas') {
        const level = this.getLevelValue(row)
        if (level === 2 || level === '2') {
          return '高'
        }
        if (level === 1 || level === '1') {
          return '中'
        }
        if (level === 0 || level === '0') {
          return '低'
        }
        const alertType = row.overType
        if (alertType === 2 || alertType === '2') {
          return '高'
        }
        if (alertType === 1 || alertType === '1') {
          return '中'
        }
        if (alertType === 0 || alertType === '0' || alertType === -1 || alertType === '-1') {
          return '低'
        }
        return '--'
      }
      if (this.activeCategory === 'hiddenDanger') {
        if (row.level === 1 || row.level === '1') {
          return '重大隐患'
        }
        if (row.level === 0 || row.level === '0') {
          return '一般隐患'
        }
      }
      return '--'
    },
    getLevelColor(row) {
      const level = this.getLevelText(row)
      if (level === '高' || level === '重大隐患') {
        return 'error'
      }
      if (level === '中' || level === '一般隐患') {
        return 'warning'
      }
      if (level === '低') {
        return 'success'
      }
      return 'default'
    },
    getAlarmTime(row) {
      if (this.activeCategory === 'gas') {
        return row.collectionTime || row.warningTime || row.alarmTime || row.gmtCreate || '--'
      }
      if (this.activeCategory === 'taskOverdue') {
        return row.planEndTime || row.gmtCreate || '--'
      }
      if (this.activeCategory === 'certificateExpiry') {
        return row.expireTime || row.gmtModified || '--'
      }
      if (this.activeCategory === 'onDuty') {
        return row.warningTime || row.createTime || row.gmtCreate || '--'
      }
      if (this.activeCategory === 'workPermit') {
        return row.planStartTime || row.gmtCreate || '--'
      }
      if (this.activeCategory === 'hiddenDanger') {
        return row.checkTime || row.createTime || row.gmtCreate || '--'
      }
      return row.createTime || row.gmtCreate || '--'
    },
    getHandleTime(row) {
      if (this.activeCategory === 'gas') {
        return row.handleTime || row.disposeTime || row.updateTime || row.gmtModified || '--'
      }
      if (this.activeCategory === 'taskOverdue') {
        return row.endTime || row.gmtModified || '--'
      }
      if (this.activeCategory === 'certificateExpiry') {
        return row.updateTime || row.gmtModified || '--'
      }
      if (this.activeCategory === 'onDuty') {
        return row.handleTime || row.disposeTime || row.updateTime || row.gmtModified || '--'
      }
      if (this.activeCategory === 'workPermit') {
        return row.updateTime || row.gmtModified || '--'
      }
      if (this.activeCategory === 'hiddenDanger') {
        return row.planRectificationTime || row.updateTime || row.gmtModified || '--'
      }
      return row.updateTime || row.gmtModified || '--'
    },
    getHandlerName(row) {
      if (this.activeCategory === 'gas') {
        if (Array.isArray(row.users)) {
          return row.users.map(item => item.nickname).join(',') || '--'
        }
        return row.handleUserName || row.disposeUserName || row.handlerName || '--'
      }
      if (this.activeCategory === 'taskOverdue') {
        return row.troubleshootEmployeeName || row.creator || '--'
      }
      if (this.activeCategory === 'certificateExpiry') {
        return row.certificateManagerName || row.nextReviewNoticeName || row.ownerEmployeeName || '--'
      }
      if (this.activeCategory === 'onDuty') {
        return row.handleUserName || row.disposeUserName || '--'
      }
      if (this.activeCategory === 'workPermit') {
        return row.noticePerson || '--'
      }
      if (this.activeCategory === 'hiddenDanger') {
        return row.creatorVo && row.creatorVo.nickname ? row.creatorVo.nickname : row.creator || '--'
      }
      return '--'
    }
  }
}
</script>

<style scoped>
.alarm-classification-page {
  padding: 16px;
  background: #f5f7f9;
  min-height: calc(100vh - 120px);
}

.alarm-classification-header,
.alarm-classification-toolbar,
.alarm-classification-content {
  background: #ffffff;
  border: 1px solid #e8eaec;
  border-radius: 6px;
}

.alarm-classification-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  margin-bottom: 16px;
}

.header-title {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.title-text {
  font-size: 18px;
  font-weight: 600;
  color: #17233d;
}

.title-desc {
  font-size: 12px;
  color: #808695;
}

.header-actions,
.toolbar-buttons {
  display: flex;
  gap: 8px;
}

.alarm-classification-toolbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 20px;
  margin-bottom: 16px;
}

.toolbar-left {
  display: grid;
  grid-template-columns: repeat(3, minmax(140px, 180px));
  gap: 12px;
}

.category-card {
  border: 1px solid #dcdee2;
  border-radius: 6px;
  padding: 12px 14px;
  cursor: pointer;
  transition: border-color 0.2s, background-color 0.2s;
}

.category-card.active {
  border-color: #2d8cf0;
  background: #f0faff;
}

.category-name {
  font-size: 14px;
  color: #17233d;
}

.category-total {
  margin-top: 8px;
  font-size: 20px;
  font-weight: 600;
  color: #2d8cf0;
}

.toolbar-right {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 12px;
}

.alarm-classification-content {
  display: flex;
  min-height: 520px;
}

.content-side {
  width: 240px;
  padding: 16px;
  border-right: 1px solid #e8eaec;
  background: #fbfbfc;
}

.side-title {
  margin-bottom: 12px;
  font-size: 14px;
  font-weight: 600;
  color: #17233d;
}

.side-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  margin-bottom: 8px;
  border-radius: 4px;
  cursor: pointer;
  color: #515a6e;
}

.side-item.active {
  background: #e8f4ff;
  color: #2d8cf0;
}

.content-main {
  flex: 1;
  min-width: 0;
  padding: 16px;
}

.content-main-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.toolbar-summary {
  display: flex;
  align-items: center;
  gap: 12px;
}

.summary-title {
  font-size: 16px;
  font-weight: 600;
  color: #17233d;
}

.summary-count {
  font-size: 12px;
  color: #808695;
}

.page-bar {
  margin-top: 16px;
  text-align: right;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: #f8f8f9;
  border-radius: 4px;
}

.detail-label,
.form-label {
  font-size: 12px;
  color: #808695;
}

.detail-value {
  font-size: 14px;
  color: #17233d;
  word-break: break-all;
}

.detail-image-box {
  margin-top: 16px;
  text-align: center;
}

.detail-image {
  max-width: 100%;
  max-height: 360px;
  border-radius: 4px;
}

.handle-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.handle-empty {
  color: #808695;
}
</style>
